import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

import { API_URL } from '../constants/urls';

type EventSourceLike = {
  onopen: ((ev: Event) => any) | null;
  onerror: ((ev: Event) => any) | null;
  readyState: number;
  addEventListener: (
    type: string,
    listener: (event: MessageEvent) => void,
  ) => void;
  close: () => void;
};

class FetchEventSource implements EventSourceLike {
  public onopen: ((ev: Event) => any) | null = null;
  public onerror: ((ev: Event) => any) | null = null;
  public readyState: number = EventSource.CONNECTING;

  private abortController = new AbortController();
  private listeners = new Map<string, Set<(event: MessageEvent) => void>>();

  constructor(
    private url: string,
    private init: {
      headers?: HeadersInit;
      credentials?: RequestCredentials;
    } = {},
  ) {
    this.connect();
  }

  public addEventListener(
    type: string,
    listener: (event: MessageEvent) => void,
  ) {
    const set = this.listeners.get(type) ?? new Set();
    set.add(listener);
    this.listeners.set(type, set);
  }

  public close() {
    if (this.readyState === EventSource.CLOSED) return;
    this.readyState = EventSource.CLOSED;
    this.abortController.abort();
    this.listeners.clear();
  }

  private dispatch(type: string, data: string) {
    const set = this.listeners.get(type);
    if (!set || set.size === 0) return;

    const event = new MessageEvent(type, { data });
    set.forEach((listener) => listener(event));
  }

  private async connect() {
    try {
      const response = await fetch(this.url, {
        method: 'GET',
        headers: this.init.headers,
        credentials: this.init.credentials,
        signal: this.abortController.signal,
      });

      if (!response.ok || !response.body) {
        if (response.status === 401 || response.status === 403) {
          this.dispatch(
            'error',
            JSON.stringify({
              error: 'Authentication failed',
              status: response.status,
            }),
          );
        } else {
          this.dispatch(
            'error',
            JSON.stringify({
              error: 'Failed to connect to notification stream',
              status: response.status,
            }),
          );
        }
        this.readyState = EventSource.CLOSED;
        if (this.onerror) {
          this.onerror(new Event('error'));
        }
        return;
      }

      this.readyState = EventSource.OPEN;
      if (this.onopen) {
        this.onopen(new Event('open'));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (this.readyState !== EventSource.CLOSED) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          this.parseAndDispatch(rawEvent);
        }
      }

      if (this.readyState !== EventSource.CLOSED) {
        this.readyState = EventSource.CLOSED;
        if (this.onerror) {
          this.onerror(new Event('error'));
        }
      }
    } catch {
      if (this.readyState !== EventSource.CLOSED) {
        this.readyState = EventSource.CLOSED;
        if (this.onerror) {
          this.onerror(new Event('error'));
        }
      }
    }
  }

  private parseAndDispatch(rawEvent: string) {
    const lines = rawEvent.split('\n');
    let eventType = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (!line || line.startsWith(':')) continue;
      if (line.startsWith('event:')) {
        eventType = line.slice('event:'.length).trim() || eventType;
        continue;
      }
      if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    }

    this.dispatch(eventType, dataLines.join('\n'));
  }
}

// Types
export enum NotificationType {
  MENTION_IN_TASK = 'mention_in_task',
  MENTION_IN_COMMENT = 'mention_in_comment',
  MENTION_IN_OKR = 'mention_in_okr',
  MENTION_IN_NARRATIVE = 'mention_in_narrative',
  MENTION_IN_PROPOSITION = 'mention_in_proposition',
  MENTION_IN_ACCOUNT_PLAN = 'mention_in_account_plan',
  ASSIGNED_TO_TASK = 'assigned_to_task',
  ASSIGNED_TO_OKR = 'assigned_to_okr',
  ASSIGNED_AS_OWNER = 'assigned_as_owner',
  OWNED_ENTITY_UPDATED = 'owned_entity_updated',
  OWNED_ENTITY_COMMENTED = 'owned_entity_commented',
  OWNED_ENTITY_STATUS_CHANGED = 'owned_entity_status_changed',
  ADDED_AS_CO_OWNER = 'added_as_co_owner',
  REMOVED_AS_CO_OWNER = 'removed_as_co_owner',
  TASK_BLOCKED = 'task_blocked',
  TASK_UNBLOCKED = 'task_unblocked',
  DEADLINE_APPROACHING = 'deadline_approaching',
  DEADLINE_PASSED = 'deadline_passed',
  KEY_RESULT_PROGRESS_UPDATE = 'key_result_progress_update',
  OKR_SCORE_CHANGED = 'okr_score_changed',
  COMPANY_STATUS_CHANGED = 'company_status_changed',
  POSITION_SCORE_CHANGED = 'position_score_changed',
}

export enum NotificationPriority {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export interface Notification {
  id: string;
  organization_id: string;
  user_id: string;
  type: NotificationType;
  priority: NotificationPriority;
  entity_type?: string;
  entity_id?: string;
  actor_id?: string;
  actor_name?: string;
  actor_email?: string;
  title: string;
  message: string;
  data?: Record<string, any>;
  notification_group_id?: string;
  group_count: number;
  read_at?: string;
  clicked_at?: string;
  archived_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  organization_id: string;
  enabled: boolean;
  do_not_disturb: boolean;
  do_not_disturb_until?: string;
  type_preferences: Record<string, boolean>;
  muted_entities: Array<{ type: string; id: string }>;
  digest_enabled: boolean;
  digest_frequency?: string;
  digest_time?: string;
  digest_day?: number;
  created_at: string;
  updated_at: string;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  isLoading: boolean;
  isConnected: boolean;
  connectionStable: boolean;
  hasLoadedOnce: boolean;
  fetchNotifications: (unreadOnly?: boolean) => Promise<void>;
  markAsRead: (notificationIds?: string[], markAll?: boolean) => Promise<void>;
  markAsClicked: (notificationId: string) => Promise<void>;
  archiveOldNotifications: (olderThanDays?: number) => Promise<void>;
  updatePreferences: (
    preferences: Partial<NotificationPreferences>,
  ) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(
  null,
);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within NotificationProvider',
    );
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { selectedOrg } = useOrganization();
  const { token } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] =
    useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStable, setConnectionStable] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false); // Track if we've successfully loaded data
  const eventSourceRef = useRef<EventSourceLike | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const lastHeartbeatRef = useRef<number>(0);
  const heartbeatCountRef = useRef(0);
  const stabilityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(
    async (unreadOnly = false) => {
      if (!selectedOrg) return;

      // Check if we have auth token
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        // No auth token available for notifications
        setNotifications([]);
        setUnreadCount(0);
        setHasLoadedOnce(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/notifications/?organization_id=${selectedOrg.id}${
            unreadOnly ? '&unread_only=true' : ''
          }`,
          { headers },
        );

        setNotifications(response.data.notifications || []);
        const count = response.data.unread_count ?? 0;
        setUnreadCount(count);
        setHasLoadedOnce(true); // Mark as successfully loaded
      } catch (error: any) {
        // Reset state on error to prevent stale data
        setNotifications([]);
        setUnreadCount(0);
        // Don't set hasLoadedOnce to true on error - this prevents phantom dot
      } finally {
        setIsLoading(false);
      }
    },
    [selectedOrg],
  );

  // Fetch unread count only
  const refreshUnreadCount = useCallback(async () => {
    if (!selectedOrg) return;

    // Check if we have auth token
    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      // No auth token for unread count refresh
      setUnreadCount(0);
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/notifications/unread-count?organization_id=${selectedOrg.id}`,
        { headers },
      );

      const count = response.data.unread_count ?? 0;
      setUnreadCount(count);
      setHasLoadedOnce(true); // Mark as successfully loaded
    } catch (error: any) {
      // Don't silently set to 0 - keep existing count on error
    }
  }, [selectedOrg]);

  // Fetch user preferences
  const fetchPreferences = useCallback(async () => {
    if (!selectedOrg) return;

    try {
      const response = await axios.get(
        `${API_URL}/notifications/preferences?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setPreferences(response.data);
    } catch (error) {
      // Error fetching preferences
    }
  }, [selectedOrg]);

  // Mark notifications as read
  const markAsRead = useCallback(
    async (notificationIds?: string[], markAll = false) => {
      if (!selectedOrg) return;

      try {
        await axios.put(
          `${API_URL}/notifications/read?organization_id=${selectedOrg.id}`,
          {
            notification_ids: notificationIds || [],
            mark_all: markAll,
          },
          { headers: getAuthHeaders() },
        );

        // Update local state for notifications
        if (markAll) {
          setNotifications((prev) =>
            prev.map((n) => ({ ...n, read_at: new Date().toISOString() })),
          );
        } else if (notificationIds) {
          setNotifications((prev) =>
            prev.map((n) =>
              notificationIds.includes(n.id)
                ? { ...n, read_at: new Date().toISOString() }
                : n,
            ),
          );
        }

        // Don't try to calculate count locally - fetch from backend for accuracy
        await refreshUnreadCount();
      } catch (error) {
        toast.error('Failed to mark notifications as read');
      }
    },
    [selectedOrg, refreshUnreadCount],
  );

  // Mark notification as clicked
  const markAsClicked = useCallback(
    async (notificationId: string) => {
      if (!selectedOrg) return;

      try {
        await axios.put(
          `${API_URL}/notifications/${notificationId}/clicked?organization_id=${selectedOrg.id}`,
          {},
          { headers: getAuthHeaders() },
        );

        // Update local state for the notification
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? {
                  ...n,
                  clicked_at: new Date().toISOString(),
                  read_at: n.read_at || new Date().toISOString(),
                }
              : n,
          ),
        );

        // Don't try to calculate count locally - fetch from backend for accuracy
        await refreshUnreadCount();
      } catch (error) {
        // Error marking notification as clicked
      }
    },
    [selectedOrg, refreshUnreadCount],
  );

  // Archive old notifications
  const archiveOldNotifications = useCallback(
    async (olderThanDays = 30) => {
      if (!selectedOrg) return;

      try {
        const response = await axios.post(
          `${API_URL}/notifications/archive-old?organization_id=${selectedOrg.id}&older_than_days=${olderThanDays}`,
          {},
          { headers: getAuthHeaders() },
        );

        toast.success(`Archived ${response.data.archived} notifications`);

        // Refresh notifications list
        await fetchNotifications();
      } catch (error) {
        toast.error('Failed to archive notifications');
      }
    },
    [selectedOrg, fetchNotifications],
  );

  // Update user preferences
  const updatePreferences = useCallback(
    async (updatedPrefs: Partial<NotificationPreferences>) => {
      if (!selectedOrg) return;

      try {
        const response = await axios.put(
          `${API_URL}/notifications/preferences?organization_id=${selectedOrg.id}`,
          updatedPrefs,
          { headers: getAuthHeaders() },
        );
        setPreferences(response.data);
        toast.success('Notification preferences updated');
      } catch (error) {
        toast.error('Failed to update preferences');
      }
    },
    [selectedOrg],
  );

  // Setup SSE connection for real-time notifications
  const setupSSE = useCallback(() => {
    // Prevent multiple connections
    if (eventSourceRef.current) {
      return;
    }

    if (!selectedOrg || !token) {
      return;
    }

    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      return;
    }

    // Create EventSource - auth is handled via cookies by API Gateway
    // The API Gateway validates Better Auth session and adds JWT header
    const sseUrl = `${API_URL}/notifications/stream?organization_id=${selectedOrg.id}`;

    // Use EventSource with credentials to send cookies for session auth
    const eventSource = new FetchEventSource(sseUrl, {
      headers,
      credentials: 'include',
    });

    eventSource.onopen = () => {
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
      // Reset heartbeat tracking for new connection
      heartbeatCountRef.current = 0;
      lastHeartbeatRef.current = Date.now();
    };

    eventSource.addEventListener('connected', () => {
      setIsConnected(true);
    });

    eventSource.addEventListener('notification', (event) => {
      try {
        const data = JSON.parse(event.data);

        // Show toast for high priority notifications
        if (
          data.notification &&
          data.notification.priority === NotificationPriority.HIGH
        ) {
          toast(data.notification.message, {
            icon: '🔔',
            duration: 5000,
          });
        }

        // Update notifications list
        if (data.notification) {
          setNotifications((prev) => [data.notification, ...prev]);
        }

        // Always refresh from backend to ensure sync
        // Don't trust the unread_count from SSE blindly
        refreshUnreadCount();
      } catch (error) {
        // Error parsing SSE notification
      }
    });

    eventSource.addEventListener('heartbeat', () => {
      // Heartbeat received, connection is alive
      setIsConnected(true);

      // Track heartbeat timing
      const now = Date.now();
      lastHeartbeatRef.current = now;
      heartbeatCountRef.current++;

      // After 2 heartbeats, consider connection stable
      if (heartbeatCountRef.current >= 2) {
        setConnectionStable(true);

        // Clear any existing stability timer
        if (stabilityTimerRef.current) {
          clearTimeout(stabilityTimerRef.current);
        }

        // Keep stable indicator for 45 seconds after last heartbeat
        stabilityTimerRef.current = setTimeout(() => {
          const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;
          if (timeSinceLastHeartbeat > 45000) {
            setConnectionStable(false);
          }
        }, 45000);
      }
    });

    // Listen for custom error events sent from the server
    eventSource.addEventListener('error', (event) => {
      try {
        // Cast to MessageEvent for SSE events that have data
        const messageEvent = event as MessageEvent;
        if (messageEvent.data) {
          const data = JSON.parse(messageEvent.data);
          // If it's an authentication error, don't try to reconnect
          if (
            data.error?.includes('Authentication') ||
            data.error?.includes('Not a member')
          ) {
            setIsConnected(false);
            eventSource.close();
            eventSourceRef.current = null;
            return;
          }
        }
      } catch {
        // Not a JSON error message, proceed with normal error handling
      }
    });

    eventSource.onerror = () => {
      setIsConnected(false);
      setConnectionStable(false);
      heartbeatCountRef.current = 0;

      // Check if the connection was closed intentionally
      if (eventSource.readyState === EventSource.CLOSED) {
        eventSourceRef.current = null;
        return;
      }

      eventSource.close();
      eventSourceRef.current = null;

      // Don't reconnect if we're cleaning up or no longer have valid auth
      if (!selectedOrg || !token) {
        return;
      }

      // Implement exponential backoff for reconnection
      const backoffDelay = Math.min(
        1000 * Math.pow(2, reconnectAttemptsRef.current),
        30000,
      );
      reconnectAttemptsRef.current++;

      // Clear any existing reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        setupSSE();
      }, backoffDelay);
    };

    eventSourceRef.current = eventSource;
  }, [selectedOrg, token, refreshUnreadCount]);

  // Clean up SSE connection
  const cleanupSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (stabilityTimerRef.current) {
      clearTimeout(stabilityTimerRef.current);
      stabilityTimerRef.current = null;
    }
    setIsConnected(false);
    setConnectionStable(false);
    heartbeatCountRef.current = 0;
  }, []);

  // Initial setup and organization changes
  useEffect(() => {
    if (selectedOrg) {
      // Force reset state before fetching to ensure clean start
      setNotifications([]);
      setUnreadCount(0);
      setHasLoadedOnce(false); // Reset loaded flag when org changes

      // Fetch fresh data
      fetchNotifications();
      fetchPreferences();
      setupSSE();
    } else {
      cleanupSSE();
      setNotifications([]);
      setUnreadCount(0);
      setHasLoadedOnce(false); // Reset loaded flag
      setPreferences(null);
    }

    return cleanupSSE;
  }, [selectedOrg]);

  // Periodic refresh of unread count (SSE provides real-time updates, this is just backup)
  useEffect(() => {
    if (!selectedOrg) return;

    const interval = setInterval(refreshUnreadCount, 300000); // Every 5 minutes (SSE handles real-time)
    return () => clearInterval(interval);
  }, [selectedOrg, refreshUnreadCount]);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    preferences,
    isLoading,
    isConnected,
    connectionStable,
    hasLoadedOnce,
    fetchNotifications,
    markAsRead,
    markAsClicked,
    archiveOldNotifications,
    updatePreferences,
    refreshUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
